import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { EVENT_NAME } from '@frontend/constants';
import { useInstanceId } from '@frontend/hooks';
import { isDesktopApp } from '@frontend/utils/desktop';

import { sendToFreshThread } from '../remote/sendToFreshThread';

/**
 * If the chat body hasn't announced itself this long after we navigated to it,
 * send anyway. Losing the prompt is worse than sending it into a thread we
 * couldn't confirm was reset — the user typed it and watched it disappear.
 */
const READY_TIMEOUT_MS = 5_000;

/**
 * Receives a prompt typed in the desktop app's quick-ask popover and sends it
 * as a user turn in a brand-new thread — never whatever thread happened to be
 * open, and never one restored from wherever the user left the app.
 *
 * The desktop shell can't send this itself (that needs the loaded,
 * authenticated app), so main forwards the text over the desktop bridge and
 * this drives the same INITIAL_PROMPT queue the header's Ask Opal uses.
 * Deliberately a react-router navigation rather than a URL round-trip: the
 * popover exists to answer a question the instant it's asked, and reloading
 * the SPA to pass a string through `?q=` costs seconds of blank page on every
 * ask. (The shell still falls back to that URL when this listener doesn't ack,
 * so an older frontend degrades to a pre-filled composer rather than dropping
 * the prompt.)
 *
 * Getting a *new* thread is the whole subtlety, and it splits on whether the
 * chat body is already mounted:
 *
 * - Already on chat — the body is live and holding a thread, so reset it
 *   synchronously before the queue can drain, or the turn lands in the open
 *   thread. Nothing is navigated. It is tempting to also clear `?threadId=`
 *   from the URL, but that makes ChatEventListener dispatch a *second* reset
 *   one render later, after the queue has already drained, which wipes the
 *   turn just sent — the prompt appears to vanish. Unnecessary anyway: the URL
 *   self-heals, because OPAL_THREAD_CREATED writes the new thread's id into it
 *   as soon as the send creates one.
 *
 * - Coming from another route — the body is unmounted, so a reset dispatched
 *   now goes nowhere (OPAL_CHAT_SET_THREAD is a transient window event) and the
 *   remounting body restores its previous thread from the persisted atom. That
 *   is why the target thread depended on the page you had left the app on.
 *   Navigate, wait for the body to announce OPAL_CHAT_READY, then reset and
 *   send.
 *
 * The bridge subscription is mount-scoped ([] deps, everything mutable read
 * through refs) and NOT keyed on `navigate`/`instanceId`. It used to be: the
 * cleanup aborts whatever send is waiting on OPAL_CHAT_READY, so any re-run —
 * and navigating is exactly what changes those deps — tore down the listener
 * microseconds after arming it, between the navigation and the body mounting.
 * The prompt was silently dropped, and only ever on the path that needed the
 * wait. Pending work must outlive a route change, so only unmount may cancel it.
 *
 * No-op on the web build and on shells too old to send the event.
 */
export function useDesktopQuickAsk() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { instanceId } = useInstanceId();

  // Read at prompt time rather than captured at subscribe time, so the
  // subscription never has to re-register (see the note above).
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const instanceIdRef = useRef(instanceId);
  instanceIdRef.current = instanceId;
  const isOnChatRef = useRef(false);
  isOnChatRef.current = pathname.endsWith('/chat');

  // A send waiting on the chat body to mount. Owned by an AbortController so a
  // second quick-ask, or unmounting, cancels it — a leaked listener would fire
  // on the next chat mount for any reason and inject a stale prompt.
  const pendingRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!isDesktopApp()) {
      return;
    }

    const off = window.desktopAPI?.onQuickAskPrompt?.(prompt => {
      const text = prompt.trim();
      const currentInstanceId = instanceIdRef.current;
      if (!text || !currentInstanceId) {
        return;
      }
      pendingRef.current?.abort();

      if (isOnChatRef.current) {
        sendToFreshThread(text);
        return;
      }

      const controller = new AbortController();
      pendingRef.current = controller;
      const send = () => {
        controller.abort(); // consume, so this can only run once
        sendToFreshThread(text);
      };
      const timer = setTimeout(send, READY_TIMEOUT_MS);
      controller.signal.addEventListener('abort', () => clearTimeout(timer));
      window.addEventListener(EVENT_NAME.OPAL_CHAT_READY, send, {
        once: true,
        signal: controller.signal,
      });
      navigateRef.current(`/instances/${currentInstanceId}/chat`);
    });

    return () => {
      off?.();
      pendingRef.current?.abort();
    };
  }, []);
}
